/* eslint-disable jsx-a11y/label-has-associated-control */

/**
 * RenderCheckboxList
 */
import React, { SyntheticEvent } from 'react';
import { Form, Input, Checkbox } from 'semantic-ui-react';

interface Props {
    input: {
        onChange: any;
        value: string;
    };
    placeholder: string;
    options?: string[];
}

interface State {
    otherSelected: boolean;
    otherInputValue: string;
}

// Find and remove item from an array
// Normalize helpers to keep tokens consistent
const normalizeToken = (s: string): string => (s || '').trim();

const splitTokens = (value?: string): string[] => {
    if (!value) return [];
    // Split by ';' then trim, allowing inconsistent spacing
    return value
        .split(';')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
};

const joinTokens = (tokens: string[]): string => {
    // Dedupe and join with a single '; ' separator
    const deduped = Array.from(
        new Set(tokens.map(normalizeToken).filter(Boolean))
    );
    return deduped.join('; ');
};

export const findAndRemove = (array: string[], value: string): string[] => {
    const target = normalizeToken(value);
    const newArray = array.filter((t) => normalizeToken(t) !== target);
    return newArray;
};

export default class RenderCheckboxList extends React.PureComponent<
    Props,
    State
> {
    state = {
        otherSelected: false,
        otherInputValue: ''
    };

    componentDidMount(): void {
        const { options } = this.props;
        if (options) {
            const [otherValue] = this.valueArray.filter(
                (item: string) => !options.includes(item)
            );
            if (otherValue) {
                this.setState({
                    otherInputValue: otherValue,
                    otherSelected: true
                });
            }
        }
    }

    get valueArray(): any {
        const {
            input: { value }
        } = this.props;
        return splitTokens(value);
    }

    addOption = (option: string): void => {
        const {
            input: { onChange }
        } = this.props;

        const current = this.valueArray;
        // Guard against duplicates
        if (current.map(normalizeToken).includes(normalizeToken(option))) {
            onChange(joinTokens(current));
            return;
        }
        const value = joinTokens(current.concat(option));
        onChange(value);
    };

    removeOption = (option: string): void => {
        const {
            input: { onChange }
        } = this.props;
        const value = joinTokens(findAndRemove(this.valueArray, option));
        onChange(value);
    };

    updateOption = (option: string): void => {
        const {
            input: { onChange, value }
        } = this.props;
        const current = splitTokens(value);
        const withoutPrev = findAndRemove(current, this.state.otherInputValue);
        const next =
            option && option.length > 0
                ? withoutPrev.concat(option)
                : withoutPrev;
        onChange(joinTokens(next));
    };

    handleOtherChange = (event: any, data: { checked: boolean }): void => {
        if (!data.checked) {
            // Remove previous 'Other' token entirely
            const {
                input: { onChange, value }
            } = this.props;
            const current = splitTokens(value);
            const withoutPrev = findAndRemove(
                current,
                this.state.otherInputValue
            );
            onChange(joinTokens(withoutPrev));
            this.setState({ otherInputValue: '' });
        }
        this.setState({ otherSelected: data.checked });
    };

    handleOtherInputChange = (event: SyntheticEvent<EventTarget>): void => {
        const { value }: any = event.target;
        // Use functional setState to derive updates atomically based on previous state
        this.setState((prev) => {
            const {
                input: { onChange, value: inputValue }
            } = this.props;
            const current = splitTokens(inputValue);
            const withoutPrev = findAndRemove(current, prev.otherInputValue);
            const next =
                value && value.length > 0
                    ? withoutPrev.concat(value)
                    : withoutPrev;
            onChange(joinTokens(next));
            return { otherInputValue: value } as State;
        });
    };

    handleInputChange = (
        event: any,
        data: { checked: boolean; name: string }
        // eslint-disable-next-line
    ): any => {
        const { checked, name } = data;

        // Restrict checkbox toggle if the other input value matches the checkbox name
        if (name === this.state.otherInputValue) return undefined;

        if (checked) {
            this.addOption(name);
        } else {
            this.removeOption(name);
        }
    };

    render(): React.ReactElement {
        const { input, placeholder, options } = this.props;
        const { otherInputValue, otherSelected } = this.state;

        if (!options || !options.length) {
            return (
                <Form.Field>
                    <Input
                        {...input}
                        value={input.value}
                        placeholder={placeholder}
                        type="text"
                    />
                </Form.Field>
            );
        }

        return (
            <React.Fragment>
                {options.map((option) => (
                    <Form.Field key={option} inline>
                        <Checkbox
                            name={option}
                            checked={this.valueArray
                                .map(normalizeToken)
                                .includes(normalizeToken(option))}
                            // @ts-ignore
                            onChange={this.handleInputChange}
                        />
                        <label>{option}</label>
                    </Form.Field>
                ))}
                <Form.Field inline>
                    <Checkbox
                        name="other"
                        // @ts-ignore
                        onChange={this.handleOtherChange}
                        checked={otherSelected}
                    />
                    <label> Other</label>
                </Form.Field>
                {otherSelected && (
                    <Form.Field>
                        <Input
                            value={otherInputValue}
                            onChange={this.handleOtherInputChange}
                            placeholder="Please specify"
                            type="text"
                        />
                    </Form.Field>
                )}
            </React.Fragment>
        );
    }
}
